'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import * as React from 'react';
import { useCallback, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/buttons/button';
import type { Locale, StaticTranslationString } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
const previousSlideText: StaticTranslationString = {
  de: 'Vorheriges Bild',
  en: 'Previous slide',
  fr: 'Image précédente'
};
const nextSlideText: StaticTranslationString = {
  de: 'Nächstes Bild',
  en: 'Next slide',
  fr: 'Image suivante'
};
type CarouselButtonProperties = React.ComponentProps<typeof Button> & {
  locale: Locale;
};
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
interface CarouselProperties {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
}
type CarouselContextProperties = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProperties;
const CarouselContext = React.createContext<CarouselContextProperties | undefined>(undefined);
const useCarousel = () => {
  const $ = _c(12);
  const context = React.useContext(CarouselContext);
  if (context === undefined) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  const {
    api
  } = context;
  let t0;
  if ($[0] !== api) {
    t0 = callback => {
      if (!api) {
        return _temp;
      }
      api.on("select", callback);
      api.on("reInit", callback);
      return () => {
        api.off("select", callback);
        api.off("reInit", callback);
      };
    };
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api) {
    t1 = () => api ? api.selectedScrollSnap() + 1 : 1;
    $[2] = api;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const current = useSyncExternalStore(t0, t1, _temp2);
  let t2;
  if ($[4] !== api) {
    t2 = callback_0 => {
      if (!api) {
        return _temp3;
      }
      api.on("reInit", callback_0);
      return () => {
        api.off("reInit", callback_0);
      };
    };
    $[4] = api;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== api) {
    t3 = () => api ? api.slideNodes().length : 0;
    $[6] = api;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const size = useSyncExternalStore(t2, t3, _temp4);
  let t4;
  if ($[8] !== context || $[9] !== current || $[10] !== size) {
    t4 = {
      ...context,
      current,
      size
    };
    $[8] = context;
    $[9] = current;
    $[10] = size;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProperties>((t0, reference) => {
  const $ = _c(46);
  let children;
  let className;
  let opts;
  let plugins;
  let properties;
  let setApi;
  let t1;
  if ($[0] !== t0) {
    ({
      orientation: t1,
      opts,
      setApi,
      plugins,
      className,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = opts;
    $[4] = plugins;
    $[5] = properties;
    $[6] = setApi;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    opts = $[3];
    plugins = $[4];
    properties = $[5];
    setApi = $[6];
    t1 = $[7];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  const t2 = orientation === "horizontal" ? "x" : "y";
  let t3;
  if ($[8] !== opts || $[9] !== t2) {
    t3 = {
      ...opts,
      axis: t2
    };
    $[8] = opts;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const [carouselReference, api] = useEmblaCarousel(t3, plugins);
  const [canScrollPrevious, setCanScrollPrevious] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = _api => {
      if (!_api) {
        return;
      }
      setCanScrollPrevious(_api.canScrollPrev());
      setCanScrollNext(_api.canScrollNext());
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const onSelect = t4;
  let t5;
  if ($[12] !== api) {
    t5 = () => {
      api?.scrollPrev();
    };
    $[12] = api;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const scrollPrevious = t5;
  let t6;
  if ($[14] !== api) {
    t6 = () => {
      api?.scrollNext();
    };
    $[14] = api;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const scrollNext = t6;
  let t7;
  if ($[16] !== scrollNext || $[17] !== scrollPrevious) {
    t7 = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrevious();
      } else {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      }
    };
    $[16] = scrollNext;
    $[17] = scrollPrevious;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleKeyDown = t7;
  let t8;
  let t9;
  if ($[19] !== api || $[20] !== setApi) {
    t8 = () => {
      if (!api || !setApi) {
        return;
      }
      setApi(api);
    };
    t9 = [api, setApi];
    $[19] = api;
    $[20] = setApi;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  React.useEffect(t8, t9);
  let t10;
  let t11;
  if ($[23] !== api) {
    t10 = () => {
      if (!api) {
        return;
      }
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api.off("select", onSelect);
      };
    };
    t11 = [api, onSelect];
    $[23] = api;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  React.useEffect(t10, t11);
  const t12 = orientation ?? (opts?.axis === "y" ? "vertical" : "horizontal");
  let t13;
  if ($[26] !== api || $[27] !== canScrollNext || $[28] !== canScrollPrevious || $[29] !== carouselReference || $[30] !== opts || $[31] !== scrollNext || $[32] !== scrollPrevious || $[33] !== t12) {
    t13 = {
      carouselRef: carouselReference,
      api,
      opts,
      orientation: t12,
      scrollPrev: scrollPrevious,
      scrollNext,
      canScrollPrev: canScrollPrevious,
      canScrollNext
    };
    $[26] = api;
    $[27] = canScrollNext;
    $[28] = canScrollPrevious;
    $[29] = carouselReference;
    $[30] = opts;
    $[31] = scrollNext;
    $[32] = scrollPrevious;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== className) {
    t14 = cn("relative", className);
    $[35] = className;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== children || $[38] !== handleKeyDown || $[39] !== properties || $[40] !== reference || $[41] !== t14) {
    t15 = <div ref={reference} onKeyDownCapture={handleKeyDown} className={t14} role="region" aria-roledescription="carousel" {...properties}>{children}</div>;
    $[37] = children;
    $[38] = handleKeyDown;
    $[39] = properties;
    $[40] = reference;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t13 || $[44] !== t15) {
    t16 = <><CarouselContext.Provider value={t13}>{t15}</CarouselContext.Provider></>;
    $[43] = t13;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
});
Carousel.displayName = 'Carousel';
const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((t0, reference) => {
  const $ = _c(13);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  const {
    carouselRef,
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("flex", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== properties || $[7] !== reference || $[8] !== t2) {
    t3 = <div ref={reference} className={t2} {...properties} />;
    $[6] = properties;
    $[7] = reference;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== carouselRef || $[11] !== t3) {
    t4 = <div ref={carouselRef} className="overflow-hidden">{t3}</div>;
    $[10] = carouselRef;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});
CarouselContent.displayName = 'CarouselContent';
const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  index: number;
}>((t0, reference) => {
  const $ = _c(19);
  let className;
  let index;
  let properties;
  if ($[0] !== t0) {
    ({
      index,
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = index;
    $[3] = properties;
  } else {
    className = $[1];
    index = $[2];
    properties = $[3];
  }
  const {
    size: maxIndex,
    current,
    orientation,
    scrollPrev,
    scrollNext
  } = useCarousel();
  let t1;
  if ($[4] !== current || $[5] !== index || $[6] !== maxIndex || $[7] !== scrollNext || $[8] !== scrollPrev) {
    t1 = () => {
      if (current - 1 === (index + 1) % maxIndex) {
        scrollPrev();
      } else {
        if (current - 1 === (index - 1 + maxIndex) % maxIndex) {
          scrollNext();
        }
      }
    };
    $[4] = current;
    $[5] = index;
    $[6] = maxIndex;
    $[7] = scrollNext;
    $[8] = scrollPrev;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const onClick = t1;
  const t2 = orientation === "horizontal" ? "pl-4" : "pt-4";
  let t3;
  if ($[10] !== className || $[11] !== t2) {
    t3 = cn("min-w-0 shrink-0 grow-0 basis-full", t2, className);
    $[10] = className;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== index || $[14] !== onClick || $[15] !== properties || $[16] !== reference || $[17] !== t3) {
    t4 = <div ref={reference} key={index} role="group" onClick={onClick} aria-roledescription="slide" className={t3} {...properties} />;
    $[13] = index;
    $[14] = onClick;
    $[15] = properties;
    $[16] = reference;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
});
CarouselItem.displayName = 'CarouselItem';
const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselButtonProperties>((t0, reference) => {
  const $ = _c(21);
  let className;
  let locale;
  let properties;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      locale,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = locale;
    $[3] = properties;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    locale = $[2];
    properties = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "outline" : t1;
  const size = t2 === undefined ? "icon" : t2;
  const {
    orientation,
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  const t3 = orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90";
  let t4;
  if ($[6] !== className || $[7] !== t3) {
    t4 = cn("absolute h-8 w-8 rounded-full", t3, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !canScrollPrev;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowLeft className="text-conveniat-green h-4 w-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = previousSlideText[locale];
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="sr-only">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== properties || $[13] !== reference || $[14] !== scrollPrev || $[15] !== size || $[16] !== t4 || $[17] !== t5 || $[18] !== t8 || $[19] !== variant) {
    t9 = <Button ref={reference} variant={variant} size={size} className={t4} disabled={t5} onClick={scrollPrev} {...properties}>{t6}{t8}</Button>;
    $[12] = properties;
    $[13] = reference;
    $[14] = scrollPrev;
    $[15] = size;
    $[16] = t4;
    $[17] = t5;
    $[18] = t8;
    $[19] = variant;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
});
CarouselPrevious.displayName = 'CarouselPrevious';
const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselButtonProperties>((t0, reference) => {
  const $ = _c(21);
  let className;
  let locale;
  let properties;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      locale,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = locale;
    $[3] = properties;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    locale = $[2];
    properties = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "outline" : t1;
  const size = t2 === undefined ? "icon" : t2;
  const {
    orientation,
    scrollNext,
    canScrollNext
  } = useCarousel();
  const t3 = orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90";
  let t4;
  if ($[6] !== className || $[7] !== t3) {
    t4 = cn("absolute h-8 w-8 rounded-full", t3, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !canScrollNext;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowRight className="text-conveniat-green h-4 w-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = nextSlideText[locale];
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="sr-only">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== properties || $[13] !== reference || $[14] !== scrollNext || $[15] !== size || $[16] !== t4 || $[17] !== t5 || $[18] !== t8 || $[19] !== variant) {
    t9 = <Button ref={reference} variant={variant} size={size} className={t4} disabled={t5} onClick={scrollNext} {...properties}>{t6}{t8}</Button>;
    $[12] = properties;
    $[13] = reference;
    $[14] = scrollNext;
    $[15] = size;
    $[16] = t4;
    $[17] = t5;
    $[18] = t8;
    $[19] = variant;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
});
CarouselNext.displayName = 'CarouselNext';
const CarouselDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  index: number;
}>((t0, reference) => {
  const $ = _c(17);
  let className;
  let index;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      index,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = index;
    $[3] = properties;
  } else {
    className = $[1];
    index = $[2];
    properties = $[3];
  }
  const {
    current
  } = useCarousel();
  const [isVisible, setIsVisible] = React.useState(false);
  let t1;
  let t2;
  if ($[4] !== current || $[5] !== index) {
    t1 = () => {
      setIsVisible(current - 1 === index);
    };
    t2 = [current, index];
    $[4] = current;
    $[5] = index;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[8] !== className) {
    t3 = cn("mx-auto w-full py-2 text-center text-xs text-balance text-gray-300 transition-opacity duration-300 lg:max-w-[75%]", className);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = isVisible ? 1 : 0;
  let t5;
  if ($[10] !== t4) {
    t5 = {
      opacity: t4
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== properties || $[13] !== reference || $[14] !== t3 || $[15] !== t5) {
    t6 = <div ref={reference} className={t3} style={t5} {...properties} />;
    $[12] = properties;
    $[13] = reference;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});
CarouselDescription.displayName = 'CarouselDescription';
export { Carousel, CarouselContent, CarouselDescription, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi };
function _temp() {}
function _temp2() {
  return 1;
}
function _temp3() {}
function _temp4() {
  return 0;
}